import type * as Three from "three";

type ThreeModule = typeof Three;

/**
 * Lighting for the 3D preview.
 *
 * Lamps alone cannot light metal. A metallic surface has no colour of its own
 * to scatter light with; all it shows is a reflection of what surrounds it.
 * With nothing around the model, a chrome frame reflects nothing and renders
 * black, however many lamps point at it. The studio below is that surrounding.
 *
 * The studio also lights every other surface, so the lamps that did the same
 * job are turned down by the amount it adds: no ambient lamp, and a quarter of
 * the hemisphere lamp. Measured on a fabric armchair, the picture's mean
 * brightness stays within half a percent of what it was with lamps only.
 *
 * Every level is per unit of the Brightness slider.
 */
export const VIEWER_LIGHT_LEVELS = {
  environment: 0.4,
  hemisphere: 0.3,
  main: 2.5,
  fill: 1.8,
  back: 1.2,
  front: 1.5,
  bottom: 1.0,
} as const;

/** Grey walls with soft boxes overhead, at both sides, and in front of and behind the model. */
export function buildStudioRoom(THREE: ThreeModule): Three.Scene {
  const room = new THREE.Scene();
  const surface = (geometry: Three.BufferGeometry, level: number, place?: (mesh: Three.Mesh) => void) => {
    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(level), side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geometry, material);
    place?.(mesh);
    room.add(mesh);
  };
  surface(new THREE.BoxGeometry(12, 12, 12), 0.35);
  surface(new THREE.PlaneGeometry(7, 7), 6, (m) => { m.position.set(0, 5.9, 0); m.rotation.x = Math.PI / 2; });
  surface(new THREE.PlaneGeometry(4, 6), 3.6, (m) => { m.position.set(-5.9, 1, 1); m.rotation.y = Math.PI / 2; });
  surface(new THREE.PlaneGeometry(4, 6), 3.6, (m) => { m.position.set(5.9, 1, -1); m.rotation.y = -Math.PI / 2; });
  surface(new THREE.PlaneGeometry(6, 4), 3, (m) => { m.position.set(0, 2, 5.9); });
  surface(new THREE.PlaneGeometry(6, 3), 1.8, (m) => { m.position.set(0, 2, -5.9); });
  return room;
}

/** The studio baked into a reflection map. It holds GPU memory until `dispose()` is called. */
export interface StudioEnvironment {
  /** For `scene.environment`. */
  texture: Three.Texture;
  dispose: () => void;
}

/**
 * Bakes the studio for `scene.environment`. The caller owns the result and
 * disposes it with the viewer.
 */
export function createStudioEnvironment(THREE: ThreeModule, renderer: Three.WebGLRenderer): StudioEnvironment {
  const room = buildStudioRoom(THREE);
  const pmrem = new THREE.PMREMGenerator(renderer);
  // The whole render target is kept, not just its texture: disposing the texture alone
  // leaves the target's framebuffer and depth attachment on the GPU.
  const target = pmrem.fromScene(room, 0.04);
  pmrem.dispose();
  room.traverse((object) => {
    const mesh = object as Three.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    (mesh.material as Three.Material).dispose();
  });
  return { texture: target.texture, dispose: () => target.dispose() };
}
